import React, { useState, useRef, useEffect } from "react";
import moment from 'moment';
import {
  Select,
  Table,
  Button,
  DatePicker,
  Pagination,
  message,
} from "antd";
import * as echarts from "echarts";
import Analysisapi from "@/api/analysis";
import stationApi from "@/api/station";

const energyMap = {
  ZDL_Y: "总电量",
  ZGLT_Y: "总功率",
  ZGL_Y: "瞬时功率",
  CDL_Y: "C项电流",
  ADL_Y: "A项电流",
  BDL_Y: "B项电流",
  ACDY_Y: "CA项电压",
  ABDY_Y: "AB项电压",
  BCDY_Y: "BC项电压",
  BDY_Y: "B项电压",
  CDY_Y: "C项电压",
  ADY_Y: "A项电压",
};

const columns = [
  {
    title: "设备名称",
    dataIndex: "deviceName",
    key: "deviceName",
    width: 120,
  },
  {
    title: "采集时间",
    dataIndex: "uniIcon",
    key: "uniIcon",
    width: 160,
  },
  ...Object.entries(energyMap).map(([key, title]) => ({
    title,
    dataIndex: key,
    key,
    width: 100,
    align: "center",
  })),
];

const { RangePicker } = DatePicker;

export default function MonomerEnergy() {
  const [device, setDevice] = useState(null);
  const [statType, setStatType] = useState("日");
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "month"),
    moment(),
  ]);
  const [viewType, setViewType] = useState("table");
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [deviceLs, setDeviceLs] = useState([]);

  const fieldNames = {
    label: "itemName",
    value: "id",
    children: "child",
  };

  // 查询设备
  const getDevice = (stationId = "61") => {
    Analysisapi.selectEnergyDeviceList({ stationId }).then((res) => {
      if (res.status !== "0") return;
      setDeviceLs(res.data ? res.data : []);
    });
  };

  useEffect(() => {
    getDevice();
  }, []);

  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      handleQuery({ pageNum: 1, pageSize });
    }
    // eslint-disable-next-line
  }, []);

  // 查询接口
  const handleQuery = async ({ pageNum, pageSize }) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning("请选择时间范围");
      return;
    }
    setLoading(true);
    try {
      const startTime = dateRange[0].format("YYYY-MM-DD HH:mm:ss");
      const endTime = dateRange[1].format("YYYY-MM-DD HH:mm:ss");
      const params = {
        deviceId: device,
        censusType: statType === "小时" ? 1 : 2,
        startTime,
        endTime,
        pageNum,
        pageSize,
      };
      const res = await Analysisapi.getEnergyReportByDevice(params);
      const rawList = res?.data?.data || [];
      const tableList = rawList.map((row, idx) => {
        const obj = {
          key: idx,
          uniIcon: row.createTime,
          deviceName: row.deviceName || "",
        };
        Object.entries(row.jsonVal).forEach((item) => {
          // 兼容映射key
          let mapKey = Object.entries(energyMap).find(([k,v]) => {
            return item[0] == v
          });
          if (mapKey && mapKey[0]) {
            obj[mapKey[0]] = item[1];
          }
        });
        return obj;
      });
      setDataSource(tableList);
      setTotal(res?.data?.total || 0);
      setPageNum(pageNum);
      setPageSize(pageSize);
    } catch (e) {
      setDataSource([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 图表数据
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState([]);

  // 查询图表数据
  const fetchChartData = async () => {
    try {
      let chartInstance = echarts.getInstanceByDom(chartRef.current);
      if (!chartInstance) {
        chartInstance = echarts.init(chartRef.current);
      }
      setChartData(dataSource);
    } catch {
      setChartData([]);
    }
  };

  useEffect(() => {
    if (viewType === "chart") {
      fetchChartData();
    }
  }, [viewType, device, statType, dateRange]);

  // 渲染图表
  useEffect(() => {
    if (viewType === "chart" && chartRef.current) {
      let chartInstance = echarts.getInstanceByDom(chartRef.current);
      if (!chartInstance) {
        chartInstance = echarts.init(chartRef.current);
      }
      chartInstance.clear();

      if (!dataSource.length) {
        // 3. 显示暂无数据
        chartInstance.setOption({
          graphic: {
            elements: [
              {
                type: "text",
                left: "center",
                top: "50%",
                style: {
                  text: "暂无数据",
                  fontSize: 16,
                  fontWeight: "normal",
                  fill: "#999", // 文本颜色
                },
                z: 100,
              },
            ],
          },
          // 清空其他配置，避免图表元素干扰
          series: [],
        });
        return;
      }

      const xData = dataSource.map((item) => item.uniIcon);
      const {key,uniIcon,deviceName,...series} = dataSource[0]
      const seriesData = Object.entries(series).reduce((ary, [key, val]) => {
        ary.push({
          name: energyMap[key],
          type: "line",
          data: dataSource.reduce((acc, it)=>{
            acc.push(it[key])
            return acc
          }, []),
        });
        return ary;
      }, []);
      const option = {
        legend: {},
        tooltip: {},
        dataset: {},
        xAxis: { type: "category", data: xData },
        yAxis: {},
        series: seriesData,
      };
      chartInstance.setOption(option);
      chartInstance.resize();
      return () => {
        if (chartInstance && chartRef.current) {
          echarts.dispose(chartRef.current);
        }
      };
    }
  }, [viewType, dataSource]);

  const setSelectedDeviceIds = (value) => {
    setDevice(value);
  };

  return (
    <div style={{ background: "#fff", padding: 24 }}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <span>设备</span>
        <Select
          placeholder="请选择巡检设备"
          value={device}
          style={{width: '180px'}}
          onChange={(e) => setSelectedDeviceIds(e)}
        >
          <Select.Option value={null}>全部设备</Select.Option>
          {deviceLs.map((item) => {
            return (
              <Select.Option value={item.deviceId} key={item.deviceId}>
                {item.deviceName}
              </Select.Option>
            );
          })}
        </Select>
        <span>统计类型</span>
        <Button
          type={statType === "小时" ? "primary" : "default"}
          onClick={() => setStatType("小时")}
        >
          小时
        </Button>
        <Button
          type={statType === "日" ? "primary" : "default"}
          onClick={() => setStatType("日")}
        >
          日
        </Button>
        <span>时间</span>
        <RangePicker
          picker={statType === "小时" ? "time" : "date"}
          showTime={statType === "小时" ? { format: "HH:mm:ss" } : false}
          value={dateRange}
          onChange={setDateRange}
          style={{ minWidth: 220 }}
          placeholder={
            statType === "小时"
              ? ["开始时间", "结束时间"]
              : ["开始日期", "结束日期"]
          }
        />
        <Button style={{ marginLeft: 8 }} onClick={() => setDateRange(null)}>
          重置
        </Button>
        <Button
          type="primary"
          onClick={() => {
            handleQuery({ pageNum, pageSize });
          }}
        >
          查询
        </Button>
      </div>
      <div style={{ marginBottom: 12 }}>
        <Button.Group>
          <Button
            type={viewType === "table" ? "primary" : "default"}
            onClick={() => setViewType("table")}
          >
            表格
          </Button>
          <Button
            type={viewType === "chart" ? "primary" : "default"}
            onClick={() => setViewType("chart")}
          >
            图表
          </Button>
        </Button.Group>
      </div>
      {viewType === "table" ? (
        <>
          <Table
            columns={columns}
            dataSource={dataSource}
            bordered
            pagination={false}
            scroll={{ x: "max-content" }}
            loading={loading}
            rowKey={(record) =>
              `${record.deviceName || ""}_${record.uniIcon || ""}`
            }
          />
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Pagination
              current={pageNum}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              showTotal={(t) => `共${t}条`}
              onChange={(page, size) =>
                handleQuery({ pageNum: page, pageSize: size })
              }
            />
          </div>
        </>
      ) : (
        <div ref={chartRef} style={{ height: 400, width: "100%" }} />
      )}
    </div>
  );
}
